// src/pages/CalendarPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Timestamp } from 'firebase/firestore'; // Solo importamos Timestamp para manejo de fechas
import { useAuth } from '../context/AuthContext';
import { calendarService } from '../services/calendarService';
import { clientService } from '../services/clientService';
import { 
    CalendarDaysIcon, ClockIcon, PlusIcon, ChevronLeftIcon, ChevronRightIcon, 
    UserIcon, BriefcaseIcon, XMarkIcon, CheckCircleIcon, TrashIcon, 
    UserPlusIcon, MagnifyingGlassIcon
} from '@heroicons/react/24/solid';

// --- UTILIDADES DE FECHA ---
const daysOfWeek = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// --- COMPONENTE MODAL DE EVENTO ---
const EventModal = ({ event, onClose, onSave, onDelete, clients, technicians, setNotification, canEdit }) => {
    const [formData, setFormData] = useState({
        title: event?.title || '',
        clientId: event?.clientId || '',
        clientName: event?.clientName || '',
        technicianId: event?.technicianId || '',
        date: event?.date ? new Date(event.date.seconds * 1000).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        time: event?.time || '09:00',
        type: event?.type || 'Visita Técnica',
        status: event?.status || 'Programada',
        notes: event?.notes || ''
    });

    // Estado para búsqueda inteligente de clientes
    const [clientSearch, setClientSearch] = useState(event?.clientName || '');
    const [showClientSuggestions, setShowClientSuggestions] = useState(false);
    const [isCreatingClient, setIsCreatingClient] = useState(false);

    // Sugerir título al seleccionar cliente y tipo
    useEffect(() => {
        if (!event && formData.clientName && formData.type) {
            setFormData(prev => ({ ...prev, title: `${formData.type} - ${formData.clientName}` }));
        }
    }, [formData.clientName, formData.type, event]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // --- LÓGICA DE BÚSQUEDA Y CREACIÓN DE CLIENTES ---
    
    const filteredClients = clients.filter(c => 
        (c.nombre || c.name || '').toLowerCase().includes((clientSearch || '').toLowerCase())
    );

    const handleClientSearchChange = (e) => {
        const value = e.target.value;
        setClientSearch(value);
        setShowClientSuggestions(true);
        if (formData.clientId) {
            setFormData(prev => ({ ...prev, clientId: '', clientName: '' }));
        }
    };

    const selectClient = (client) => {
        setFormData(prev => ({ ...prev, clientId: client.id, clientName: client.nombre || client.name }));
        setClientSearch(client.nombre || client.name);
        setShowClientSuggestions(false);
    };

    const createAndSelectClient = async () => {
        if (!clientSearch.trim()) return;
        setIsCreatingClient(true);
        try {
            // Usamos el servicio de calendario para crear el cliente rápido (Pendiente)
            const newClient = await calendarService.createQuickClient(clientSearch);
            
            // Seleccionar en el formulario localmente
            selectClient(newClient);
            
            setNotification({ message: 'Cliente rápido creado. Recuerde completar su ficha después.', type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al crear cliente.', type: 'error' });
        } finally {
            setIsCreatingClient(false);
            setShowClientSuggestions(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.clientId || !formData.technicianId) {
            setNotification({ message: 'Cliente y Técnico son obligatorios.', type: 'error' });
            return;
        }
        // Convertir fecha a Timestamp para guardado
        const eventDate = new Date(`${formData.date}T${formData.time}`);
        onSave({
            ...formData,
            date: Timestamp.fromDate(eventDate)
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] border border-transparent dark:border-gray-700 transition-colors">
                <div className="bg-indigo-600 dark:bg-indigo-700 p-4 flex justify-between items-center text-white flex-shrink-0 transition-colors">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <CalendarDaysIcon className="w-6 h-6"/> 
                        {event ? 'Editar Cita' : 'Nueva Cita / Mantención'}
                    </h3>
                    <button onClick={onClose}><XMarkIcon className="w-6 h-6 hover:text-gray-200 dark:hover:text-gray-300 transition-colors"/></button>
                </div>
                
                <div className="overflow-y-auto p-6">
                    <form id="eventForm" onSubmit={handleSubmit} className="space-y-4">
                        
                        {/* SELECTOR INTELIGENTE DE CLIENTE */}
                        <div className="relative">
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Cliente</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                </div>
                                <input
                                    type="text"
                                    value={clientSearch}
                                    onChange={handleClientSearchChange}
                                    onFocus={() => setShowClientSuggestions(true)}
                                    placeholder="Buscar o crear cliente..."
                                    className={`w-full border dark:border-gray-600 rounded p-2 pl-10 focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 outline-none transition-colors ${!formData.clientId && clientSearch ? 'border-yellow-400 dark:border-yellow-500' : ''}`}
                                    disabled={!canEdit}
                                    autoComplete="off"
                                />
                                {formData.clientId && (
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400" />
                                    </div>
                                )}
                            </div>

                            {/* Dropdown de Sugerencias */}
                            {showClientSuggestions && clientSearch && canEdit && (
                                <div className="absolute z-20 w-full bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-b-lg shadow-xl max-h-48 overflow-y-auto mt-1 transition-colors">
                                    {filteredClients.length > 0 ? (
                                        filteredClients.map(c => (
                                            <div 
                                                key={c.id}
                                                onClick={() => selectClient(c)}
                                                className="px-4 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 cursor-pointer text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2 border-b border-gray-50 dark:border-gray-700 transition-colors"
                                            >
                                                <UserIcon className="w-4 h-4 text-gray-400 dark:text-gray-500"/>
                                                {c.nombre || c.name}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                            No existe este cliente.
                                        </div>
                                    )}
                                    
                                    {/* Opción de Crear Nuevo */}
                                    {(!filteredClients.some(c => (c.nombre || c.name || '').toLowerCase() === clientSearch.toLowerCase())) && (
                                        <div 
                                            onClick={createAndSelectClient}
                                            className="px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 cursor-pointer text-sm text-indigo-700 dark:text-indigo-400 font-bold border-t dark:border-gray-700 flex items-center gap-2 sticky bottom-0 transition-colors"
                                        >
                                            <UserPlusIcon className="w-4 h-4"/>
                                            {isCreatingClient ? 'Creando...' : `Crear Nuevo: "${clientSearch}"`}
                                        </div>
                                    )}
                                </div>
                            )}
                            {showClientSuggestions && (
                                <div className="fixed inset-0 z-10" onClick={() => setShowClientSuggestions(false)}></div>
                            )}
                        </div>

                        {/* Tipo y Estado */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Tipo Trabajo</label>
                                <select name="type" value={formData.type} onChange={handleChange} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500 transition-colors" disabled={!canEdit}>
                                    <option>Visita Técnica</option>
                                    <option>Mantención Preventiva</option>
                                    <option>Emergencia / Correctivo</option>
                                    <option>Instalación</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Estado</label>
                                <select name="status" value={formData.status} onChange={handleChange} className={`w-full border dark:border-gray-600 rounded p-2 font-bold bg-white dark:bg-gray-700 outline-none focus:ring-2 focus:ring-indigo-500 transition-colors ${formData.status === 'Completada' ? 'text-green-600 dark:text-green-500' : formData.status === 'Cancelada' ? 'text-red-600 dark:text-red-500' : 'text-indigo-600 dark:text-indigo-400'}`} disabled={!canEdit}>
                                    <option value="Programada" className="text-gray-900 dark:text-gray-100">Programada</option>
                                    <option value="Confirmada" className="text-gray-900 dark:text-gray-100">Confirmada</option>
                                    <option value="En Proceso" className="text-gray-900 dark:text-gray-100">En Proceso</option>
                                    <option value="Completada" className="text-gray-900 dark:text-gray-100">Completada</option>
                                    <option value="Cancelada" className="text-gray-900 dark:text-gray-100">Cancelada</option>
                                </select>
                            </div>
                        </div>

                        {/* Fecha y Hora */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Fecha</label>
                                <input type="date" name="date" value={formData.date} onChange={handleChange} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500 transition-colors [color-scheme:light_dark]" required disabled={!canEdit}/>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Hora</label>
                                <input type="time" name="time" value={formData.time} onChange={handleChange} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500 transition-colors [color-scheme:light_dark]" required disabled={!canEdit}/>
                            </div>
                        </div>

                        {/* Técnico Asignado */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Técnico Asignado</label>
                            <select name="technicianId" value={formData.technicianId} onChange={handleChange} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500 transition-colors" disabled={!canEdit}>
                                <option value="">-- Asignar Técnico --</option>
                                {technicians.map(t => <option key={t.id} value={t.id}>{t.displayName || t.email}</option>)}
                            </select>
                        </div>

                        {/* Notas */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Notas / Detalles</label>
                            <textarea name="notes" value={formData.notes} onChange={handleChange} className="w-full border dark:border-gray-600 rounded p-2 h-20 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500 transition-colors" placeholder="Detalles del trabajo, contacto en sitio..." disabled={!canEdit}></textarea>
                        </div>
                    </form>
                </div>

                {/* Acciones Footer */}
                {canEdit && (
                    <div className="flex justify-between p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0 transition-colors">
                        {event ? (
                            <button type="button" onClick={() => onDelete(event.id)} className="text-red-500 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 font-bold flex items-center text-sm transition-colors">
                                <TrashIcon className="w-4 h-4 mr-1"/> Eliminar
                            </button>
                        ) : <div></div>}
                        
                        <div className="flex gap-2">
                            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">Cancelar</button>
                            <button type="submit" form="eventForm" className="px-6 py-2 bg-indigo-600 dark:bg-indigo-700 text-white font-bold rounded hover:bg-indigo-700 dark:hover:bg-indigo-600 shadow transition-colors">Guardar Cita</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- PÁGINA PRINCIPAL CALENDARIO ---
const CalendarPage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [clients, setClients] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [view, setView] = useState('month');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // --- PERMISOS ---
    const isSuperUser = user?.role === 'superuser' || user?.email === 'nelson.jtapia@gmail.com';
    const isAdmin = user?.role === 'admin' || isSuperUser;
    const perms = user?.permissions?.calendar || {};
    
    const canView = isAdmin || perms.view;
    const canCreate = isAdmin || perms.create;
    const canEdit = isAdmin || perms.edit;

    // --- CARGA DE DATOS ---
    useEffect(() => {
        let unsubscribeEvents = null;

        const fetchData = async () => {
            setLoading(true);
            try {
                // 1. Cargar Clientes (Usando el servicio de clientes)
                const clientsData = await clientService.getAll();
                setClients(clientsData);

                // 2. Cargar Técnicos (Usando el servicio de calendario)
                const techsData = await calendarService.getTechnicians();
                setTechnicians(techsData);

                // 3. Cargar Eventos en Tiempo Real (Usando el servicio de calendario)
                unsubscribeEvents = calendarService.subscribeToEvents(
                    (eventsData) => {
                        setEvents(eventsData);
                        setLoading(false);
                    },
                    (error) => {
                        console.error("Error events snapshot:", error);
                        setNotification({ message: 'Error en conexión del calendario.', type: 'error' });
                        setLoading(false);
                    }
                );

            } catch (error) {
                console.error("Error loading calendar data:", error);
                setNotification({ message: 'Error cargando datos del calendario.', type: 'error' });
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            if (unsubscribeEvents) unsubscribeEvents();
        };
    }, [setNotification]);

    // --- NAVEGACIÓN CALENDARIO ---
    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const goToToday = () => setCurrentDate(new Date());

    // --- LÓGICA DEL GRID MENSUAL ---
    const calendarGrid = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        
        const firstDayOfMonth = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        const startingDay = firstDayOfMonth.getDay(); // 0 (Dom) - 6 (Sab)
        
        const days = [];
        // Relleno previo
        for (let i = 0; i < startingDay; i++) {
            days.push(null);
        }
        // Días del mes
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        return days;
    }, [currentDate]);

    // --- CRUD EVENTOS ---
    const handleSaveEvent = async (eventData) => {
        try {
            // Usamos el servicio para guardar (Crear o Editar)
            await calendarService.saveEvent(
                eventData, 
                user.email, 
                selectedEvent?.id // Si hay evento seleccionado, es edición
            );
            
            setNotification({ 
                message: selectedEvent ? 'Cita actualizada.' : 'Cita programada exitosamente.', 
                type: 'success' 
            });
            
            setShowModal(false);
            setSelectedEvent(null);
        } catch (error) {
            setNotification({ message: 'Error al guardar cita.', type: 'error' });
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('¿Eliminar esta cita del calendario?')) return;
        try {
            await calendarService.deleteEvent(eventId);
            setNotification({ message: 'Cita eliminada.', type: 'success' });
            setShowModal(false);
            setSelectedEvent(null);
        } catch (error) {
            setNotification({ message: 'Error al eliminar.', type: 'error' });
        }
    };

    // --- RENDERIZADO ---

    const getEventsForDay = (date) => {
        if (!date) return [];
        return events.filter(e => {
            const eDate = e.date.toDate();
            return eDate.getDate() === date.getDate() &&
                   eDate.getMonth() === date.getMonth() &&
                   eDate.getFullYear() === date.getFullYear();
        });
    };

    if (!canView) return <div className="p-10 text-center text-red-600 dark:text-red-400">No tienes acceso al calendario.</div>;

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen flex flex-col transition-colors duration-300">
            {showModal && (
                <EventModal 
                    event={selectedEvent}
                    onClose={() => { setShowModal(false); setSelectedEvent(null); }}
                    onSave={handleSaveEvent}
                    onDelete={handleDeleteEvent}
                    clients={clients}
                    technicians={technicians}
                    setNotification={setNotification}
                    canEdit={canEdit || (selectedEvent === null && canCreate)}
                />
            )}

            {/* HEADER CALENDARIO */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-transparent dark:border-gray-700 transition-colors">
                <div className="flex items-center gap-4">
                    <button onClick={() => setPage('hub')} className="text-indigo-600 dark:text-indigo-400 hover:underline font-semibold text-sm transition-colors">&larr; Volver</button>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2 transition-colors">
                        <CalendarDaysIcon className="w-8 h-8 text-indigo-600 dark:text-indigo-500"/> 
                        Agenda {months[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h1>
                </div>
                
                <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg transition-colors">
                    <button onClick={prevMonth} className="p-1 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-600 rounded shadow-sm transition"><ChevronLeftIcon className="w-5 h-5"/></button>
                    <button onClick={goToToday} className="text-xs font-bold px-3 py-1 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-600 rounded transition">Hoy</button>
                    <button onClick={nextMonth} className="p-1 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-600 rounded shadow-sm transition"><ChevronRightIcon className="w-5 h-5"/></button>
                </div>

                <div className="flex gap-2">
                    <button onClick={() => setView('month')} className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${view === 'month' ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>Mes</button>
                    <button onClick={() => setView('agenda')} className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${view === 'agenda' ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>Agenda</button>
                    
                    {canCreate && (
                        <button onClick={() => { setSelectedEvent(null); setShowModal(true); }} className="bg-indigo-600 dark:bg-indigo-700 text-white px-4 py-2 rounded-lg font-bold hover:bg-indigo-700 dark:hover:bg-indigo-600 shadow flex items-center gap-2 ml-2 transition-colors">
                            <PlusIcon className="w-5 h-5"/> <span className="hidden md:inline">Agendar</span>
                        </button>
                    )}
                </div>
            </div>

            {/* VISTA MENSUAL */}
            {view === 'month' && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden flex-grow flex flex-col border border-transparent dark:border-gray-700 transition-colors">
                    {/* Días Semana */}
                    <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-700/50 border-b dark:border-gray-700 transition-colors">
                        {daysOfWeek.map(d => (
                            <div key={d} className="py-2 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">{d}</div>
                        ))}
                    </div>
                    
                    {/* Grid */}
                    <div className="grid grid-cols-7 flex-grow auto-rows-fr border-l dark:border-gray-700 border-t dark:border-gray-700 transition-colors">
                        {calendarGrid.map((date, i) => {
                            const dayEvents = getEventsForDay(date);
                            const isToday = date && date.toDateString() === new Date().toDateString();

                            return (
                                <div 
                                    key={i} 
                                    className={`min-h-[100px] border-b dark:border-gray-700 border-r dark:border-gray-700 p-2 transition relative group ${!date ? 'bg-gray-50 dark:bg-gray-800' : 'hover:bg-indigo-50/30 dark:hover:bg-indigo-900/10'}`}
                                    onClick={() => {
                                        if (date && canCreate) {
                                            setSelectedEvent({ date: Timestamp.fromDate(date) }); // Pre-set date
                                            setShowModal(true);
                                        }
                                    }}
                                >
                                    {date && (
                                        <>
                                            <div className={`text-sm font-medium mb-1 ${isToday ? 'bg-indigo-600 dark:bg-indigo-500 text-white w-6 h-6 flex items-center justify-center rounded-full' : 'text-gray-700 dark:text-gray-300'}`}>
                                                {date.getDate()}
                                            </div>
                                            <div className="space-y-1 z-10 relative">
                                                {dayEvents.map(ev => (
                                                    <div 
                                                        key={ev.id}
                                                        onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); setShowModal(true); }}
                                                        className={`text-[10px] p-1 rounded truncate cursor-pointer shadow-sm border-l-2 ${
                                                            ev.type === 'Mantención Preventiva' ? 'bg-green-100 dark:bg-green-900/30 border-green-500 text-green-800 dark:text-green-300' :
                                                            ev.type === 'Emergencia / Correctivo' ? 'bg-red-100 dark:bg-red-900/30 border-red-500 text-red-800 dark:text-red-300' :
                                                            'bg-blue-100 dark:bg-blue-900/30 border-blue-500 text-blue-800 dark:text-blue-300'
                                                        }`}
                                                    >
                                                        <span className="font-bold">{ev.time}</span> {ev.title}
                                                    </div>
                                                ))}
                                            </div>
                                            {/* Botón + invisible (hover) para añadir rápido */}
                                            {canCreate && (
                                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition z-0">
                                                    <PlusIcon className="w-4 h-4 text-indigo-400 dark:text-indigo-500"/>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* VISTA AGENDA (LISTA) */}
            {view === 'agenda' && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden flex-grow border border-transparent dark:border-gray-700 transition-colors">
                    <div className="p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 font-bold text-gray-700 dark:text-gray-200 transition-colors">Próximos Eventos</div>
                    <div className="divide-y divide-gray-100 dark:divide-gray-700 transition-colors">
                        {events
                            .sort((a, b) => a.date - b.date)
                            .filter(e => e.date.toDate() >= new Date(new Date().setHours(0,0,0,0)))
                            .map(ev => (
                                <div key={ev.id} onClick={() => { setSelectedEvent(ev); setShowModal(true); }} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer flex items-center gap-4 transition-colors">
                                    <div className="flex-shrink-0 flex flex-col items-center bg-indigo-50 dark:bg-indigo-900/30 p-2 rounded-lg w-16 transition-colors">
                                        <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase">{months[ev.date.toDate().getMonth()].substr(0, 3)}</span>
                                        <span className="text-xl font-bold text-gray-800 dark:text-gray-100">{ev.date.toDate().getDate()}</span>
                                    </div>
                                    <div className="flex-grow">
                                        <div className="flex justify-between items-start">
                                            <h4 className="font-bold text-gray-800 dark:text-gray-100 text-lg">{ev.title}</h4>
                                            <span className={`px-2 py-0.5 rounded text-xs font-bold border ${
                                                ev.status === 'Completada' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/50' : 
                                                ev.status === 'Cancelada' ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50' : 
                                                'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800/50'
                                            }`}>{ev.status}</span>
                                        </div>
                                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                                            <span className="flex items-center gap-1"><ClockIcon className="w-4 h-4"/> {ev.time}</span>
                                            <span className="flex items-center gap-1"><BriefcaseIcon className="w-4 h-4"/> {ev.clientName}</span>
                                            <span className="flex items-center gap-1"><UserIcon className="w-4 h-4"/> Técnico: {technicians.find(t => t.id === ev.technicianId)?.displayName || '...'}</span>
                                        </div>
                                        {ev.notes && <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 italic line-clamp-1">{ev.notes}</p>}
                                    </div>
                                </div>
                            ))
                        }
                        {events.length === 0 && <div className="p-8 text-center text-gray-400 dark:text-gray-500">No hay eventos programados próximamente.</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarPage;